import React, { useState, useEffect } from 'react';
import {Image, message, Modal, Upload} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { DndContext, PointerSensor, useSensor } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import type { DragEndEvent } from '@dnd-kit/core';
import type { RcFile } from 'antd/es/upload';
import type { UploadFile, UploadProps } from 'antd';
import emiter, {_EVENTS} from "../../../events";

interface DraggableUploadListItemProps {
	originNode: React.ReactElement<any, string | React.JSXElementConstructor<any>>;
	file: UploadFile<any>;
}

type PropsType = {
	action?: any;
	name?: any;
	value?: any;
	onChange?: any;
	maxNum?: any;
	fileSize?: any;
	hideTitle?: boolean;
	accept?: string;
};

const getBase64 = (file: RcFile): Promise<string> =>
	new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.readAsDataURL(file);
		reader.onload = () => resolve(reader.result as string);
		reader.onerror = error => reject(error);
	});

const DraggableUploadListItem = ({ originNode, file }: DraggableUploadListItemProps) => {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
		id: file.uid,
	});

	const style: React.CSSProperties = {
		transform: CSS.Transform.toString(transform),
		transition,
		cursor: 'move',
	};

	return (
		<div
			ref={setNodeRef}
			style={style}
			// prevent preview event when drag end
			className={isDragging ? 'is-dragging' : ''}
			{...attributes}
			{...listeners}
		>
			{/* hide error tooltip when dragging */}
			{file.status === 'error' && isDragging ? originNode.props.children : originNode}
		</div>
	);
};

// TODO:repeat code
const SPUploadPhotos: React.FC<PropsType> = props => {
	const { onChange, value, maxNum, fileSize, hideTitle, accept } = props;

	const [previewOpen, setPreviewOpen] = useState(false);
	const [previewImage, setPreviewImage] = useState('');
	const [previewTitle, setPreviewTitle] = useState('');
	const [uploading, setUploading] = useState(false);

	const beforeUpload = (file: RcFile) => {
		const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
		if (!isJpgOrPng) {
			message.error('仅支持JPG/PNG格式!');
			return Upload.LIST_IGNORE;
		}
		const isLt2M = file.size / 1024 / 1024 < (fileSize || 5);
		if (!isLt2M) {
			message.error('图片需小于' + fileSize + 'MB!');
		}

		if (!isJpgOrPng || !isLt2M) return Upload.LIST_IGNORE;
		setUploading(true);
	};

	useEffect(() => {
		emiter.emit(_EVENTS.COMMON_EMIT.COMMON_UPLOAD_EMIT, uploading)
	}, [uploading]);

	const initFileList = (val: any) => {
		if (val)
			return val.map((f: any) => {
				const url = typeof f === 'string' ? f : f.file_url;
				return {
					uid: url,
					name: url,
					url,
					status: 'done',
					response: {
						data: f,
					},
				};
			});
		else return [];
	};
	const [myFileList, setMyFileList] = useState<UploadFile[]>([]);

	const handleCancel = () => setPreviewOpen(false);

	const handlePreview = async (file: UploadFile) => {
		if (!file.url && !file.preview) {
			file.preview = await getBase64(file.originFileObj as RcFile);
		}

		setPreviewImage(file.url || (file.preview as string));
		setPreviewOpen(true);
		setPreviewTitle(file.name || file.url!.substring(file.url!.lastIndexOf('/') + 1));
	};

	const handleChange: UploadProps['onChange'] = ({ fileList: newFileList }) => {
		const item = newFileList?.find?.((f: any) => f?.status === 'uploading');
		if (item) {
			setUploading(true)
		} else {
			setUploading(false)
		}
		// 格式：[{status: 'done'}, {status: 'done'}]
		setMyFileList(newFileList);

		// 都上传完毕，没有uploading
		if (newFileList.find((f: any) => f.status != 'done') == undefined) {
			let errFile = newFileList.find((f: any) => f?.response?.data == null);

			if (errFile) message.error(errFile?.response?.msg || errFile?.response?.errmsg);

			let list = newFileList.filter((f: any) => f?.response?.data != null);

			setMyFileList(list);

			// 格式：[url, url, url]
			onChange(list.map((f: any) => f?.response?.data));
		}
	};

	const sensor = useSensor(PointerSensor, {
		activationConstraint: { distance: 10 },
	});

	const onDragEnd = ({ active, over }: DragEndEvent) => {
		if (active.id !== over?.id) {
			setMyFileList((prev: any) => {
				const activeIndex = prev.findIndex((i: any) => i.uid === active.id);
				const overIndex = prev.findIndex((i: any) => i.uid === over?.id);
				const fileList: any = arrayMove(prev, activeIndex, overIndex);

				onChange(fileList.map((f: any) => f?.response?.data));

				return fileList;
			});
		}
	};

  useEffect(() => {
		setMyFileList(initFileList(value))
	}, [value]);

	const uploadButton = (
		<div>
			<PlusOutlined />
			<div style={{ marginTop: 8 }}>上传</div>
		</div>
	);
	return (
		<DndContext sensors={[sensor]} onDragEnd={onDragEnd}>
			<SortableContext items={myFileList.map(i => i.uid)} strategy={verticalListSortingStrategy}>
				<Upload
					name="file"
					beforeUpload={beforeUpload}
					action={props?.action}
					fileList={myFileList}
					onChange={handleChange}
					onPreview={handlePreview}
					listType="picture-card"
					accept={accept}
					showUploadList={true}
					itemRender={(originNode: any, file: any) => (
						<DraggableUploadListItem originNode={originNode} file={file} />
					)}
				>
					{myFileList.length >= (maxNum || 10) ? null : uploadButton}
				</Upload>
        {
          previewOpen && <Image
                preview={{
                  visible: previewOpen,
                  onVisibleChange: (visible) => setPreviewOpen(visible),
                }}
                wrapperStyle={{ display: 'none' }}
                alt="example" src={previewImage} />
        }
			</SortableContext>
		</DndContext>
	);
};

export default SPUploadPhotos;
